import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, FileText, ArrowRight, Package, ShieldCheck, Users, Clock, Lock, ExternalLink, ShoppingBag, ChevronRight } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { products } from '../data/products';
import { ProductPlaceholder } from '../components/ProductPlaceholder';
import { FormSelect } from '../components/FormSelect';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { getShopFrameworkUrl, getShopProductUrl } from '../data/shopUrls';
import { PartnerCTA } from '../components/PartnerCTA';
export function FrameworksPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    org: '',
    orgType: '',
    sector: '',
    purpose: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };
  const frameworkProducts = products.slice(0, 3);
  const frameworks = [{
    name: 'BlueLight Commercial',
    tag: 'Emergency Services',
    accentColor: 'cg-orange-600',
    audience: 'Police forces, Fire & Rescue services, and Ambulance trusts across the UK.',
    coverage: 'Uniform, protective clothing, personal protective equipment (PPE), and operational accessories.',
    sectors: ['Police', 'Fire & Rescue', 'Ambulance']
  }, {
    name: 'YPO Framework',
    tag: 'Public Sector Wide',
    accentColor: 'navy-900',
    audience: 'Local government, education, emergency services, and other public sector bodies.',
    coverage: 'Emergency response equipment, facilities management supplies, and specialist clothing.',
    sectors: ['Local Government', 'Education', 'Emergency Services']
  }];
  return <>
      <PageHero label="Procurement" title="Approved Procurement Frameworks" subtitle="Chiltern Global is appointed to key public sector frameworks, providing compliant, efficient, and transparent routes to market for government and emergency services buyers." backgroundImage="https://cdn.magicpatterns.com/uploads/cJmcybkRNgCksh1S8QiVuQ/What_we_do.jpg" data-id="element-771" />

      <Breadcrumbs items={[{
      label: 'Manufacturing',
      to: '/manufacturing'
    }, {
      label: 'Our Frameworks'
    }]} data-id="element-772" />

      {/* How Frameworks Help Buyers */}
      <section className="bg-white py-20" aria-labelledby="frameworks-benefits-heading" data-id="element-773">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-774">
          <div className="max-w-3xl mb-14" data-id="element-775">
            <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-3 block" data-id="element-776">
              Why Use Frameworks
            </span>
            <h2 id="frameworks-benefits-heading" className="text-3xl font-bold text-navy-900 mb-6" data-id="element-777">
              How Frameworks Help Buyers
            </h2>
            <p className="text-lg text-steel-600 leading-relaxed" data-id="element-778">
              Procurement frameworks reduce the time, cost, and risk associated
              with public sector purchasing. By using pre-tendered agreements,
              buyers can access Chiltern Global's manufacturing capabilities
              with the assurance that due diligence, price benchmarking, and
              compliance checks have already been conducted.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-779">
            <div className="bg-white p-8 border border-steel-200 border-l-4 border-l-cg-orange-600 rounded-sm shadow-sm hover:shadow-md transition-shadow" data-id="element-780">
              <div className="w-14 h-14 bg-cg-orange-600/10 rounded-sm flex items-center justify-center mb-6" data-id="element-781">
                <ShieldCheck className="w-7 h-7 text-cg-orange-600" aria-hidden="true" data-id="element-782" />
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-783">
                Compliant & Auditable
              </h3>
              <p className="text-sm text-steel-600 leading-relaxed" data-id="element-784">
                Fully compliant with OJEU/PCR 2015 regulations, ensuring a
                transparent audit trail for all procurement activities.
              </p>
            </div>
            <div className="bg-white p-8 border border-steel-200 border-l-4 border-l-cg-orange-600 rounded-sm shadow-sm hover:shadow-md transition-shadow" data-id="element-785">
              <div className="w-14 h-14 bg-cg-orange-600/10 rounded-sm flex items-center justify-center mb-6" data-id="element-786">
                <Users className="w-7 h-7 text-cg-orange-600" aria-hidden="true" data-id="element-787" />
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-788">
                Pre-Agreed Terms
              </h3>
              <p className="text-sm text-steel-600 leading-relaxed" data-id="element-789">
                Standardised terms and conditions and benchmarked pricing
                structures protect buyers and ensure value for money.
              </p>
            </div>
            <div className="bg-white p-8 border border-steel-200 border-l-4 border-l-cg-orange-600 rounded-sm shadow-sm hover:shadow-md transition-shadow" data-id="element-790">
              <div className="w-14 h-14 bg-cg-orange-600/10 rounded-sm flex items-center justify-center mb-6" data-id="element-791">
                <Clock className="w-7 h-7 text-cg-orange-600" aria-hidden="true" data-id="element-792" />
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-3" data-id="element-793">
                Speed to Market
              </h3>
              <p className="text-sm text-steel-600 leading-relaxed" data-id="element-794">
                Significantly reduced procurement timescales with options for
                direct award or simplified mini-competition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works — Step Process */}
      <section className="bg-slate-50 py-16 border-y border-steel-200" aria-labelledby="how-it-works-heading" data-id="element-795">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-796">
          <h2 id="how-it-works-heading" className="text-xl font-bold text-navy-900 mb-10 text-center" data-id="element-797">
            How Framework Procurement Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-798">
            {[{
            step: '01',
            title: 'Identify Framework',
            desc: 'Select the relevant framework for your organisation and requirement.'
          }, {
            step: '02',
            title: 'Review Catalogue',
            desc: 'Browse available products and specifications within the framework lots.'
          }, {
            step: '03',
            title: 'Request Details',
            desc: 'Contact our team for pricing, lot information and call-off documentation.'
          }, {
            step: '04',
            title: 'Place Order',
            desc: 'Proceed via direct award or mini-competition through the framework.'
          }].map((item, i) => <div key={i} className="relative text-center" data-id="element-799">
                <div className="w-14 h-14 bg-navy-900 text-white font-bold text-lg rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-800">
                  {item.step}
                </div>
                <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-801">
                  {item.title}
                </h3>
                <p className="text-xs text-steel-600 leading-relaxed" data-id="element-802">
                  {item.desc}
                </p>
                {i < 3 && <ChevronRight className="hidden md:block absolute top-7 -right-3 w-5 h-5 text-steel-300" aria-hidden="true" data-id="element-803" />}
              </div>)}
          </div>
        </div>
      </section>

      {/* Framework Cards */}
      <section className="bg-white py-24" aria-labelledby="framework-appointments-heading" data-id="element-804">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-805">
          <div className="mb-12" data-id="element-806">
            <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-3 block" data-id="element-807">
              Current Appointments
            </span>
            <h2 id="framework-appointments-heading" className="text-3xl font-bold text-navy-900" data-id="element-808">
              Our Framework Appointments
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20" data-id="element-809">
            {frameworks.map((fw, index) => <div key={index} className="bg-white border border-steel-200 rounded-sm overflow-hidden shadow-sm hover:shadow-lg transition-shadow flex flex-col" data-id="element-810">
                {/* Header Band */}
                <div className={`${index === 0 ? 'bg-cg-orange-600' : 'bg-navy-900'} px-10 py-8`} data-id="element-811">
                  <span className="text-xs font-bold uppercase tracking-widest text-white/70 block mb-2" data-id="element-812">
                    {fw.tag}
                  </span>
                  <h3 className="text-2xl font-bold text-white" data-id="element-813">{fw.name}</h3>
                </div>

                {/* Content */}
                <div className="p-10 flex flex-col flex-1" data-id="element-814">
                  <div className="space-y-6 mb-8 flex-1" data-id="element-815">
                    <div data-id="element-816">
                      <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-817">
                        Who is this for?
                      </h4>
                      <p className="text-steel-600 text-sm leading-relaxed" data-id="element-818">
                        {fw.audience}
                      </p>
                    </div>
                    <div data-id="element-819">
                      <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-820">
                        What is covered?
                      </h4>
                      <p className="text-steel-600 text-sm leading-relaxed" data-id="element-821">
                        {fw.coverage}
                      </p>
                    </div>
                    <div data-id="element-822">
                      <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-3" data-id="element-823">
                        Eligible Sectors
                      </h4>
                      <div className="flex flex-wrap gap-2" data-id="element-824">
                        {fw.sectors.map((sector, si) => <span key={si} className="text-[11px] font-bold uppercase tracking-wider bg-slate-100 text-navy-900 px-3 py-1.5 rounded-sm border border-steel-200" data-id="element-825">
                            {sector}
                          </span>)}
                      </div>
                    </div>
                  </div>

                  <a href="#request-framework-details" className={`inline-flex items-center justify-center px-6 py-3.5 font-bold uppercase tracking-wider text-sm rounded-sm transition-colors w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cg-orange-600 focus-visible:ring-offset-2 ${index === 0 ? 'bg-cg-orange-600 text-white hover:bg-cg-orange-500' : 'bg-navy-900 text-white hover:bg-cg-orange-600'}`} data-id="element-826">
                    Request Framework Details
                  </a>
                </div>
              </div>)}
          </div>

          {/* Framework Relevant Categories */}
          <div data-id="element-827">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-828">
              <h3 className="text-lg font-bold text-navy-900 flex items-center gap-2" data-id="element-829">
                <Package className="w-5 h-5 text-cg-orange-600" aria-hidden="true" data-id="element-830" />
                Framework-Aligned Categories
              </h3>
              <a href={getShopFrameworkUrl()} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-cg-orange-600 uppercase tracking-wider flex items-center gap-2 hover:text-navy-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cg-orange-600 focus-visible:ring-offset-2 rounded-sm" data-id="element-831">
                Browse framework-relevant categories in Inventory
                <ExternalLink className="w-4 h-4" aria-hidden="true" data-id="element-832" />
                <span className="sr-only" data-id="element-833">(opens in new tab)</span>
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-834">
              {frameworkProducts.map(product => <a key={product.slug} href={getShopProductUrl(product.slug)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-5 bg-slate-50 p-5 border border-steel-200 hover:border-cg-orange-600 rounded-sm transition-all hover:shadow-sm group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cg-orange-600 focus-visible:ring-offset-2" data-id="element-835">
                  <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded-sm border border-steel-200 bg-white" data-id="element-836">
                    <ProductPlaceholder aspectRatio="1/1" className="w-full h-full" data-id="element-837" />
                  </div>
                  <div data-id="element-838">
                    <h4 className="text-sm font-bold text-navy-900 group-hover:text-cg-orange-600 transition-colors mb-1" data-id="element-839">
                      {product.categoryName}
                    </h4>
                    <span className="text-xs text-steel-500 flex items-center gap-1" data-id="element-840">
                      View in Inventory{' '}
                      <ExternalLink className="w-3 h-3" aria-hidden="true" data-id="element-841" />
                    </span>
                  </div>
                </a>)}
            </div>
          </div>
        </div>
      </section>

      {/* Request Form — Dark Treatment */}
      <section id="request-framework-details" className="bg-navy-900 py-24" aria-labelledby="request-form-heading" data-id="element-842">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-843">
          <div className="text-center mb-12" data-id="element-844">
            <FileText className="w-12 h-12 text-cg-orange-600 mx-auto mb-4" aria-hidden="true" data-id="element-845" />
            <h2 id="request-form-heading" className="text-3xl font-bold text-white mb-4" data-id="element-846">
              Request Framework Pack
            </h2>
            <p className="text-steel-400 text-lg" data-id="element-847">
              Complete the form below to receive our comprehensive framework
              guide, including specific lot numbers, pricing structures, and
              buyer guides.
            </p>
          </div>

          {!submitted ? <form onSubmit={handleSubmit} className="space-y-6 bg-navy-800 p-8 md:p-10 border border-white/10 rounded-sm" aria-label="Framework pack request form" data-id="element-848">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-849">
                <div data-id="element-850">
                  <label htmlFor="firstName" className="block text-sm font-bold text-steel-300 mb-2" data-id="element-851">
                    First Name <span className="text-cg-orange-600" data-id="element-852">*</span>
                  </label>
                  <input type="text" id="firstName" required autoComplete="given-name" className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600" value={formData.firstName} onChange={handleChange} data-id="element-853" />
                </div>
                <div data-id="element-854">
                  <label htmlFor="lastName" className="block text-sm font-bold text-steel-300 mb-2" data-id="element-855">
                    Last Name <span className="text-cg-orange-600" data-id="element-856">*</span>
                  </label>
                  <input type="text" id="lastName" required autoComplete="family-name" className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600" value={formData.lastName} onChange={handleChange} data-id="element-857" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-858">
                <div data-id="element-859">
                  <label htmlFor="email" className="block text-sm font-bold text-steel-300 mb-2" data-id="element-860">
                    Work Email <span className="text-cg-orange-600" data-id="element-861">*</span>
                  </label>
                  <input type="email" id="email" required autoComplete="email" className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600" value={formData.email} onChange={handleChange} data-id="element-862" />
                </div>
                <div data-id="element-863">
                  <label htmlFor="telephone" className="block text-sm font-bold text-steel-300 mb-2" data-id="element-864">
                    Telephone
                  </label>
                  <input type="tel" id="telephone" autoComplete="tel" className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600" value={formData.telephone} onChange={handleChange} data-id="element-865" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-866">
                <div data-id="element-867">
                  <label htmlFor="org" className="block text-sm font-bold text-steel-300 mb-2" data-id="element-868">
                    Organisation <span className="text-cg-orange-600" data-id="element-869">*</span>
                  </label>
                  <input type="text" id="org" required autoComplete="organization" className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600" value={formData.org} onChange={handleChange} data-id="element-870" />
                </div>
                <div data-id="element-871">
                  <FormSelect id="orgType" label="Organisation Type" variant="dark" value={formData.orgType} onChange={handleChange} data-id="element-872">
                    <option value="" data-id="element-873">Select Type (Optional)</option>
                    <option value="Public sector buyer" data-id="element-874">
                      Public sector buyer
                    </option>
                    <option value="Defence buyer" data-id="element-875">Defence buyer</option>
                    <option value="Police force" data-id="element-876">Police force</option>
                    <option value="Custodial/prison service" data-id="element-877">
                      Custodial/prison service
                    </option>
                    <option value="Medical/NGO procurement" data-id="element-878">
                      Medical/NGO procurement
                    </option>
                    <option value="Private sector" data-id="element-879">Private sector</option>
                    <option value="Other" data-id="element-880">Other</option>
                  </FormSelect>
                </div>
              </div>

              <div data-id="element-881">
                <FormSelect id="sector" label="Sector" required variant="dark" value={formData.sector} onChange={handleChange} data-id="element-882">
                  <option value="" data-id="element-883">Select Sector</option>
                  <option value="Defence" data-id="element-884">Defence</option>
                  <option value="Police" data-id="element-885">Police</option>
                  <option value="Prisons" data-id="element-886">Prisons</option>
                  <option value="Medical" data-id="element-887">Medical</option>
                  <option value="Other" data-id="element-888">Other Public Sector</option>
                </FormSelect>
              </div>

              <div data-id="element-889">
                <label htmlFor="purpose" className="block text-sm font-bold text-steel-300 mb-2" data-id="element-890">
                  Purpose of Request{' '}
                  <span className="text-cg-orange-600" data-id="element-891">*</span>
                </label>
                <textarea id="purpose" required rows={4} className="w-full bg-navy-900 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-steel-500 focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600" placeholder="e.g. Upcoming tender, market engagement, pricing enquiry, framework call-off..." value={formData.purpose} onChange={handleChange} data-id="element-892" />
              </div>

              <button type="submit" className="w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900" data-id="element-893">
                Request Framework Pack
              </button>

              <div className="flex items-start gap-2" data-id="element-894">
                <Lock className="w-3.5 h-3.5 text-cg-orange-600 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-895" />
                <p className="text-xs text-steel-500 leading-relaxed" data-id="element-896">
                  This form is encrypted and all submissions are treated in
                  strict confidence. Your data is processed in accordance with
                  our{' '}
                  <Link to="/privacy-policy" className="underline hover:text-cg-orange-600 focus-visible:text-cg-orange-600" data-id="element-897">
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link to="/terms-of-service" className="underline hover:text-cg-orange-600 focus-visible:text-cg-orange-600" data-id="element-898">
                    Terms of Service
                  </Link>
                  .
                </p>
              </div>
            </form> : <div className="bg-navy-800 border border-white/10 p-12 rounded-sm text-center" role="status" data-id="element-899">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" aria-hidden="true" data-id="element-900" />
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-901">
                Request Received
              </h3>
              <p className="text-steel-400 mb-8 text-lg" data-id="element-902">
                Thank you. Your request for the framework pack has been logged.
                A member of our frameworks team will review your submission and
                send the relevant documentation to your email address.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-903">
                <Link to="/products" className="px-6 py-3 bg-cg-orange-600 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cg-orange-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900" data-id="element-904">
                  View Products
                </Link>
                <button onClick={() => setSubmitted(false)} className="px-6 py-3 border border-white/30 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900" data-id="element-905">
                  Submit Another Request
                </button>
              </div>
            </div>}
        </div>
      </section>

      {/* CTA */}
      <PartnerCTA data-id="element-906" />
    </>;
}